import {
    Attachment,
    Client,
    ClientFilters,
    ClientWithDeal,
    Comment,
    Deal,
    DealStatusCatalog,
    DealStatusId,
    FunnelPoint,
    BaseStats, AttachmentKind,
} from './types';
import { STATUS_CATALOG_DEFAULT, STATUS_ORDER } from './constants';
import {baseHost} from "../../constants/common";

export const getStatus = (catalog: DealStatusCatalog, id: DealStatusId) =>
    (catalog || STATUS_CATALOG_DEFAULT).find(s => s.id === id)
    ?? STATUS_CATALOG_DEFAULT.find(s => s.id === id)!;

export function joinClientData(
    clients: Client[],
    deals: Deal[],
    comments: Comment[],
    attachments: Attachment[],
): ClientWithDeal[] {
    const dealByClient = new Map<string, Deal>();
    deals.forEach(d => dealByClient.set(d.clientId, d));

    const lastCommentByClient = new Map<string, Comment>();
    [...comments]
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .forEach(c => {
            if (!lastCommentByClient.has(c.clientId)) lastCommentByClient.set(c.clientId, c);
        });

    const attachmentsByClient = new Map<string, Attachment[]>();
    attachments.forEach(a => {
        const list = attachmentsByClient.get(a.clientId) ?? [];
        list.push(a);
        attachmentsByClient.set(a.clientId, list);
    });

    return clients.map(c => ({
        ...c,
        deal: dealByClient.get(c.id) ?? null,
        lastComment: lastCommentByClient.get(c.id) ?? null,
        attachments: attachmentsByClient.get(c.id) ?? [],
    }));
}

export function computeStats(
    rows: ClientWithDeal[],
): BaseStats {
    const totalClients = rows.length;
    const inProgressDeals = rows.reduce(
        (sum, row) => sum + Number(row.inProgressDealsCount ?? 0),
        0,
    );
    const completedDeals = rows.reduce(
        (sum, row) => sum + Number(row.completedDealsCount ?? 0),
        0,
    );
    const completedDealsPropertyPriceSum = rows.reduce(
        (sum, row) => sum + Number(row.completedDealsPropertyPriceSum ?? 0),
        0,
    );

    return {
        totalClients,
        inProgressDeals,
        completedDeals,
        completedDealsPropertyPriceSum,
    };
}

export function computeFunnel(
    rows: ClientWithDeal[],
    catalog: DealStatusCatalog,
): FunnelPoint[] {
    const counts = new Map<DealStatusId, number>();
    STATUS_ORDER.forEach(id => counts.set(id, 0));

    rows.forEach(r => {
        const id = r.deal?.statusId ?? r.currentDealStatusId;
        if (id) counts.set(id, (counts.get(id) ?? 0) + 1);
    });

    return STATUS_ORDER.map(id => {
        const st = getStatus(catalog, id);
        return { id, label: st.name, count: counts.get(id) ?? 0, color: st.color };
    });
}

export function filterClients(rows: ClientWithDeal[], filters: ClientFilters): ClientWithDeal[] {
    const q = (filters.search || '').trim().toLowerCase();
    const status = filters.status;

    return rows.filter(r => {
        const statusOk = status === 'all' || r.deal?.statusId === status || r.currentDealStatusId === status;

        if (!q) return statusOk;
        const hay = [
            r.fullName,
            r.phone, r.email, r.telegram,
            r.queryText,
            r.lastComment?.text,
        ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

        return statusOk && hay.includes(q);
    });
}

export function buildLink(source: AttachmentKind | Deal['finalSelection']['source'], id: string): string {
    if (source === 'calculation') {
        return `${baseHost}/shared/housing/${id}`;
    }
    if (source === 'selection' || source === 'ai_selection') {
        return `${baseHost}/collection/${id}`;
    }
    return '#';
}

export function formatMoney(value: number): string {
    if (value === null || value === undefined) return '—';
    try {
        return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(value);
    } catch {
        // если приходит дробное ежемесячное — покажем без валюты
        return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(value);
    }
}
